import { Box, SelectChangeEvent, Typography, styled } from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import Button from '../../../components/common/Button';
import Notification from '../../../components/common/Notification';
import SelectField from '../../../components/common/SelectField';
import TextField from '../../../components/common/TextField';
import { PAGE_IDS, PERMISSION_IDS } from '../../../constants/permissions';
import { usePermissions } from '../../../context/PermissionContext';
import { useNotification } from '../../../hooks/useNotification';
import { applicantService } from '../../../services/applicant.service';

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Subtitle = styled(Typography)({
  fontSize: '15px',
  marginBottom: '24px',
  textAlign: 'left',
  fontWeight: 500,
  color: '#0000008C',
  fontFamily: 'Inter',
});

const FormContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '16px',
  marginBottom: '24px',
  width: '40%',
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '32px',
  width: '40%',
  padding: '0 12px',
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns YYYY-MM-DD boundaries for the current calendar month. */
function getCurrentMonthRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0); // last day of month
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: fmt(start), endDate: fmt(end) };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SendMessagePage: React.FC = () => {
  const { hasPermission, pageId, setPageId } = usePermissions();
  const { notification, showNotification, hideNotification } = useNotification();

  const [applicantId, setApplicantId] = useState<string>('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const [applicantOptions, setApplicantOptions] = useState<{ value: number; label: string }[]>([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);

  useEffect(() => {
    if (pageId !== PAGE_IDS.SEND_MESSAGE) {
      setPageId(PAGE_IDS.SEND_MESSAGE);
    }
  }, [pageId, setPageId]);

  // Fetch current-month applicants for the dropdown
  useEffect(() => {
    let cancelled = false;
    const fetchApplicants = async () => {
      setLoadingApplicants(true);
      try {
        const { startDate, endDate } = getCurrentMonthRange();
        const response = await applicantService.search({
          startDate,
          endDate,
          page: 1,
          limit: 100,
          sort: 'createdAt:desc',
        });
        if (!cancelled) {
          const options = (response.data.data ?? []).map((a) => ({
            value: a.id,
            label: `${a.firstName} ${a.lastName}`.trim(),
          }));
          setApplicantOptions(options);
        }
      } catch (error) {
        if (!cancelled) {
          showNotification('Failed to load applicants.', 'error', 'Load Error');
        }
      } finally {
        if (!cancelled) {
          setLoadingApplicants(false);
        }
      }
    };
    fetchApplicants();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setApplicantId('');
    setMessage('');
  };

  const handleSend = async () => {
    if (!applicantId || !message.trim()) return;

    try {
      setSending(true);
      await applicantService.testSendMessage(Number(applicantId), message.trim());
      showNotification('Message queued successfully via WhatsApp.', 'success', 'Message Sent');
      handleReset();
    } catch (error: unknown) {
      const detail =
        error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      showNotification(detail, 'error', 'Send Failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box className="page-container">
      <Typography className="page-title">Send Message</Typography>
      <Box className="edit-section">
        <Subtitle>
          Select a test applicant, type a message, and send it via WhatsApp.
        </Subtitle>

        <FormContainer>
          <SelectField
            label="Applicant"
            required
            value={applicantId}
            onChange={(event: SelectChangeEvent<unknown>) => {
              setApplicantId(event.target.value as string);
            }}
            options={applicantOptions}
            placeholder={loadingApplicants ? 'Loading applicants…' : 'Select applicant'}
            disabled={loadingApplicants}
          />

          <TextField
            id="message"
            label="Message"
            required
            value={message}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMessage(e.target.value)}
            multiline
            rows={5}
            placeholder="Type the WhatsApp message to send…"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                height: 'auto',
              },
            }}
          />
        </FormContainer>
      </Box>

      {hasPermission(PERMISSION_IDS.SEND_MESSAGE_ACCESS) && (
        <ButtonContainer>
          <Button
            width="175px"
            variant="outlined"
            onClick={handleReset}
            disabled={sending}
          >
            Clear
          </Button>
          <Button
            width="175px"
            variant="contained"
            onClick={handleSend}
            disabled={!applicantId || !message.trim() || sending}
            isLoading={sending}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </ButtonContainer>
      )}

      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default SendMessagePage;
